import { memo, useCallback, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, ExternalLink, FileSpreadsheet, RefreshCw, Unplug } from 'lucide-react'
import {
  announcementsApi,
  type SheetInspection,
  type SheetSyncResult,
  type SheetSyncState,
  type SheetSyncSummary,
} from '../../lib/infoserviceApi.js'
import { Spinner } from '../common/Spinner.js'
import { sheetSyncAgo, sheetSyncChanges } from '../../lib/sheetSyncText.js'

interface Props {
  channelId: string | null
  canManage: boolean
  /** Appelé après une synchronisation qui a modifié les annonces. */
  onSynced?: () => void
}

type Step =
  | { kind: 'idle' }
  | { kind: 'inspected'; inspection: SheetInspection; sheetName: string; preview: SheetSyncResult | null }

/**
 * Infoservice › Réglages › Feuille Google : la feuille « Infographies » de la
 * secrétaire, lue toutes les 5 minutes par son lien de partage. TrafficPlay ne
 * l'écrit jamais.
 */
export const SheetSyncSettings = memo(function SheetSyncSettings({ channelId, canManage, onSynced }: Props) {
  const [state, setState] = useState<SheetSyncState | null>(null)
  const [url, setUrl] = useState('')
  const [step, setStep] = useState<Step>({ kind: 'idle' })
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [lastResult, setLastResult] = useState<SheetSyncResult | null>(null)

  const refresh = useCallback(async () => {
    if (!channelId) return
    try {
      setState(await announcementsApi.sheetSync.state(channelId))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Lecture impossible')
    }
  }, [channelId])

  useEffect(() => { void refresh() }, [refresh])

  const act = useCallback(async (label: string, action: () => Promise<void>) => {
    setBusy(label)
    setError('')
    try {
      await action()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Opération impossible')
    } finally {
      setBusy(null)
    }
  }, [])

  const preview = useCallback((inspection: SheetInspection, sheetName: string) => act('preview', async () => {
    if (!channelId) return
    setStep({ kind: 'inspected', inspection, sheetName, preview: null })
    const result = await announcementsApi.sheetSync.preview(channelId, url, sheetName)
    setStep({ kind: 'inspected', inspection, sheetName, preview: result })
  }), [act, channelId, url])

  const inspect = useCallback(() => act('inspect', async () => {
    if (!channelId) return
    const inspection = await announcementsApi.sheetSync.inspect(channelId, url)
    if (!inspection.suggested) {
      setStep({ kind: 'inspected', inspection, sheetName: '', preview: null })
      return
    }
    setStep({ kind: 'inspected', inspection, sheetName: inspection.suggested, preview: null })
    const result = await announcementsApi.sheetSync.preview(channelId, url, inspection.suggested)
    setStep({ kind: 'inspected', inspection, sheetName: inspection.suggested, preview: result })
  }), [act, channelId, url])

  const connect = useCallback(() => act('connect', async () => {
    if (!channelId || step.kind !== 'inspected' || !step.sheetName) return
    const { state: next, result } = await announcementsApi.sheetSync.connect(channelId, url, step.sheetName)
    setState(next)
    setLastResult(result)
    setStep({ kind: 'idle' })
    setEditing(false)
    onSynced?.()
  }), [act, channelId, onSynced, step, url])

  const runNow = useCallback((force: boolean) => act(force ? 'force' : 'run', async () => {
    if (!channelId) return
    if (force && !window.confirm('Appliquer la feuille malgré l’avertissement ? Les annonces seront mises à jour telles que la feuille les montre.')) return
    const { state: next, result } = await announcementsApi.sheetSync.run(channelId, force)
    setState(next)
    setLastResult(result)
    if (result.applied) onSynced?.()
  }), [act, channelId, onSynced])

  const toggleEnabled = useCallback(() => act('enabled', async () => {
    if (!channelId || !state) return
    setState(await announcementsApi.sheetSync.setEnabled(channelId, !state.enabled))
  }), [act, channelId, state])

  const disconnect = useCallback(() => act('disconnect', async () => {
    if (!channelId) return
    if (!window.confirm('Débrancher la feuille ? Les annonces restent avec leurs jours et leurs images, et redeviennent modifiables dans TrafficPlay. Plus rien ne sera lu dans la feuille.')) return
    setState(await announcementsApi.sheetSync.disconnect(channelId))
    setLastResult(null)
    onSynced?.()
  }), [act, channelId, onSynced])

  if (!channelId) return null
  const connected = Boolean(state?.connected)
  const showForm = !connected || editing

  return (
    <section className="slideshow-card slideshow-card--wide sheet-sync" aria-labelledby="sheet-sync-title">
      <header className="slideshow-card__header">
        <div>
          <h3 id="sheet-sync-title"><FileSpreadsheet size={16} aria-hidden="true" /> Feuille Google des annonces</h3>
          <p className="slideshow-muted">
            La secrétaire continue de remplir sa feuille « Infographies ». TrafficPlay la relit toutes les 5 minutes, sans jamais
            l’écrire, et vous n’avez plus qu’à relier les images.
          </p>
        </div>
        {connected && state?.editUrl && (
          <a className="tp-button" href={state.editUrl} target="_blank" rel="noreferrer">
            <ExternalLink size={14} aria-hidden="true" /> Ouvrir la feuille
          </a>
        )}
      </header>

      {error && <div className="slideshow-message is-error" role="alert">{error}</div>}

      {connected && state && !editing && (
        <ConnectedState
          state={state}
          lastResult={lastResult}
          canManage={canManage}
          busy={busy}
          onRun={runNow}
          onToggle={toggleEnabled}
          onChange={() => { setEditing(true); setUrl(state.sourceUrl ?? ''); setStep({ kind: 'idle' }) }}
          onDisconnect={disconnect}
        />
      )}

      {showForm && canManage && (
        <div className="sheet-sync__connect">
          <label className="tp-form-field sheet-sync__url">
            <span className="tp-field-label">Adresse du classeur</span>
            <input
              className="tp-input"
              value={url}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              onChange={event => { setUrl(event.target.value); setStep({ kind: 'idle' }) }}
            />
          </label>
          <div className="sheet-sync__actions">
            <button type="button" className="tp-button" disabled={busy !== null || url.trim().length < 10} onClick={() => void inspect()}>
              {busy === 'inspect' && <Spinner />} Lire le classeur
            </button>
            {editing && <button type="button" className="tp-button" onClick={() => { setEditing(false); setStep({ kind: 'idle' }) }}>Annuler</button>}
          </div>
          <p className="slideshow-hint">
            Le classeur doit être partagé en « Tous les utilisateurs disposant du lien », lecteur. TrafficPlay n’y écrit rien.
          </p>

          {step.kind === 'inspected' && (
            <div className="sheet-sync__inspection">
              <div className="sheet-sync__tabs" role="radiogroup" aria-label="Onglet à synchroniser">
                {step.inspection.tabs.map(tab => (
                  <button
                    key={tab.sheetName}
                    type="button"
                    role="radio"
                    aria-checked={step.sheetName === tab.sheetName}
                    className={`sheet-sync__tab${step.sheetName === tab.sheetName ? ' is-active' : ''}`}
                    disabled={!tab.usable || busy !== null}
                    onClick={() => void preview(step.inspection, tab.sheetName)}
                  >
                    <span className="sheet-sync__tab-name">{tab.sheetName}</span>
                    <span className="slideshow-muted">
                      {tab.usable
                        ? `${tab.rows} lignes · jours cochés en`
                        : tab.problem}
                      {tab.usable && tab.checkedFill && <span className="sheet-sync__swatch" style={{ background: `#${tab.checkedFill}` }} aria-label={`couleur #${tab.checkedFill}`} />}
                    </span>
                  </button>
                ))}
              </div>
              {!step.inspection.suggested && <p className="slideshow-message is-error">Aucun onglet n’a la forme de la feuille des infographies.</p>}
              {busy === 'preview' && <p className="slideshow-muted"><Spinner /> Calcul de l’aperçu…</p>}
              {step.preview?.summary && (
                <div className="sheet-sync__preview">
                  <p className="sheet-sync__lead">En reliant cet onglet :</p>
                  <SummaryLines summary={step.preview.summary} firstConnection={!connected} />
                  {step.preview.message && <p className="slideshow-message is-warning">{step.preview.message}</p>}
                  <div className="sheet-sync__actions">
                    <button type="button" className="tp-button tp-button--primary" disabled={busy !== null} onClick={() => void connect()}>
                      {busy === 'connect' && <Spinner />} Relier cette feuille
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!connected && !canManage && <p className="slideshow-hint">Aucune feuille reliée.</p>}
    </section>
  )
})

const ConnectedState = memo(function ConnectedState({ state, lastResult, canManage, busy, onRun, onToggle, onChange, onDisconnect }: {
  state: SheetSyncState
  lastResult: SheetSyncResult | null
  canManage: boolean
  busy: string | null
  onRun: (force: boolean) => void
  onToggle: () => void
  onChange: () => void
  onDisconnect: () => void
}) {
  const status = state.lastStatus
  const summary = lastResult?.summary ?? state.lastSummary
  return (
    <div className="sheet-sync__state">
      <div className={`sheet-sync__status is-${(status ?? 'none').toLowerCase()}`} role="status">
        {status === 'OK' ? <CheckCircle2 size={16} aria-hidden="true" /> : status ? <AlertTriangle size={16} aria-hidden="true" /> : <RefreshCw size={16} aria-hidden="true" />}
        <div>
          <strong>
            {status === 'OK' ? 'À jour' : status === 'REFUSED' ? 'Synchronisation refusée — rien n’a été modifié' : status === 'ERROR' ? 'Feuille illisible' : 'En attente de la première lecture'}
          </strong>
          <span className="slideshow-muted">
            Onglet « {state.sheetName} » · {state.rowCount} annonce{state.rowCount > 1 ? 's' : ''}
            {state.lastSuccessAt ? ` · lue ${sheetSyncAgo(state.lastSuccessAt)}` : ''}
            {state.enabled ? ` · relue toutes les ${state.intervalMinutes} min` : ' · synchronisation automatique en pause'}
          </span>
          {state.lastMessage && <span className="sheet-sync__reason">{state.lastMessage}</span>}
        </div>
      </div>

      {summary && <SummaryLines summary={summary} firstConnection={false} />}

      {canManage && (
        <div className="sheet-sync__actions">
          <button type="button" className="tp-button tp-button--primary" disabled={busy !== null} onClick={() => onRun(false)}>
            {busy === 'run' ? <Spinner /> : <RefreshCw size={14} aria-hidden="true" />} Synchroniser maintenant
          </button>
          {status === 'REFUSED' && (
            <button type="button" className="tp-button" disabled={busy !== null} onClick={() => onRun(true)}>
              {busy === 'force' && <Spinner />} Appliquer quand même
            </button>
          )}
          <label className="slideshow-switch">
            <input type="checkbox" checked={state.enabled} disabled={busy !== null} onChange={onToggle} />
            <span>Relire automatiquement</span>
          </label>
          <span className="sheet-sync__spacer" />
          <button type="button" className="tp-button" disabled={busy !== null} onClick={onChange}>Changer de feuille</button>
          <button type="button" className="tp-button tp-button--danger-outline" disabled={busy !== null} onClick={onDisconnect}>
            <Unplug size={14} aria-hidden="true" /> Débrancher
          </button>
        </div>
      )}
    </div>
  )
})

const SummaryLines = memo(function SummaryLines({ summary, firstConnection }: { summary: SheetSyncSummary; firstConnection: boolean }) {
  const lines = sheetSyncChanges(summary, { firstConnection })
  return (
    <div className="sheet-sync__summary">
      <ul className="sheet-sync__changes">
        {lines.map(line => (
          <li key={line.label}>
            <span className="sheet-sync__count">{line.count}</span>
            <span>
              {line.label}
              {line.names.length > 0 && <span className="slideshow-muted"> — {line.names.join(', ')}{line.more ? '…' : ''}</span>}
            </span>
          </li>
        ))}
      </ul>
      {summary.warnings.length > 0 && (
        <details className="sheet-sync__warnings">
          <summary>{summary.warnings.length} ligne{summary.warnings.length > 1 ? 's' : ''} à reprendre dans la feuille</summary>
          <ul>
            {summary.warnings.map(warning => (
              <li key={`${warning.sheetRow}-${warning.message}`}>
                <span className="slideshow-mono">ligne {warning.sheetRow}</span> {warning.name} — {warning.message}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
})
